/**
 * 🚀 Hook pour mémoriser les handlers/callbacks de façon stable
 * Évite les re-renders causés par la re-création des functions
 */

import { useCallback, DependencyList } from 'react';

type AnyFunction = (...args: any[]) => any;

interface StableHandlers {
  [key: string]: AnyFunction;
}

/**
 * Hook pour stabiliser un objet de handlers
 * Usage: const handlers = useStableHandlers({ onClick, onClose }, [dep1, dep2])
 */
export function useStableHandlers<T extends StableHandlers>(
  handlers: T,
  deps: DependencyList
): T {
  const stableHandlers = {} as T;
  
  // Mémoriser chaque handler individuellement
  Object.keys(handlers).forEach(key => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    stableHandlers[key as keyof T] = useCallback(
      handlers[key as keyof T],
      deps
    ) as T[keyof T];
  });
  
  return stableHandlers;
}

/**
 * Hook pour stabiliser un handler unique
 * Usage: const stableHandler = useStableHandler(handler, [dep1, dep2])
 */
export function useStableHandler<T extends AnyFunction>(
  handler: T,
  deps: DependencyList
): T {
  return useCallback(handler, deps);
}